import React, { Component } from 'react';
import PropTypes from 'prop-types';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import { USER_ROLE_APARTMENT_LOCATOR } from 'common/userRoles';
import {
  LEAD_STATUS_LIST,
  LEAD_STATUS_ASSIGNED,
  LEAD_STATUS_LEASE_SIGNED,
} from 'common/leadConstants';
import { SCREEN_ID_APARTMENT_LOCATOR_LEADS_LIST } from 'client/constants/screenIds';
import {
  APARTMENT_LOCATOR_LEADS_SORT_BY_STATUS_DATE,
  APARTMENT_LOCATOR_LEADS_SORT_BY_MOVE_DATE,
  APARTMENT_LOCATOR_LEADS_SORT_BY_LEASE_START_DATE,
} from 'common/apartmentLocatorHelpers';

import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrAgentDashboardTabBar from 'client/components/SrAgentDashboardTabBar';
import SrAgentLeadsHeader from 'client/components/SrAgentLeadsHeader';
import SrAgentLeadsList from 'client/components/SrAgentLeadsList';
import SrAgentLeadHighFiveModal from 'client/components/SrAgentLeadHighFiveModal';
import SrDialogActionModal, {
  SR_DIALOG_ACTION_THEME_ACTIVE,
} from 'client/components/SrDialogActionModal';

import ApartmentLocatorActions from 'client/actions/ApartmentLocator';
import UserActions from 'client/actions/User';
import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles from 'client/helpers/allowedRoles';

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_APARTMENT_LOCATOR],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_APARTMENT_LOCATOR_LEADS_LIST)
@branch({
  appUrl: ['env', 'APP_URL'],
  currentUser: ['currentUser'],
})
class AgentLeadsPage extends Component {
  static propTypes = {
    match: PropTypes.object.isRequired,
    appUrl: PropTypes.string.isRequired,
    currentUser: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      searchText: '',
      sortCriteria:
        props.currentUser.settings.leadSortCriteria || APARTMENT_LOCATOR_LEADS_SORT_BY_STATUS_DATE,
      showSortCriteriaModal: false,
      showModalAnimation: false,
      userMoveInDateRange: [momentWithAustinTimezone().startOf('month').toISOString()],
    };

    this._isUnmounting = false;
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(ApartmentLocatorActions.clearLeadNotifications);
    this._isUnmounting = true;
  }

  _onSearchText = (searchText) => {
    this.setState({ searchText });
  };

  _onClickSortCriteriaOption = (sortCriteria) => {
    if (sortCriteria !== this.state.sortCriteria) {
      const { dispatch } = this.props;
      dispatch(ApartmentLocatorActions.resetLeadsTable);
      dispatch(UserActions.updateSettings, {
        leadSortCriteria: sortCriteria,
      });
      this.setState({
        sortCriteria,
        showSortCriteriaModal: false,
      });
    }
  };

  _toggleShowModalAnimation = () => {
    this.setState({ showModalAnimation: !this.state.showModalAnimation });

    setTimeout(() => {
      if (!this._isUnmounting) {
        this.setState({ showModalAnimation: !this.state.showModalAnimation });
      }
    }, 2300);
  };

  _toggleSortCriteriaModal = () => {
    this.setState({
      showSortCriteriaModal: !this.state.showSortCriteriaModal,
    });
  };

  _onUpdateLeadStatus = ({ showPositiveFeedbackModal }) => {
    if (!showPositiveFeedbackModal) {
      return;
    }

    this.setState({ showModalAnimation: !this.state.showModalAnimation });

    setTimeout(() => {
      if (!this._isUnmounting) {
        this.setState({ showModalAnimation: !this.state.showModalAnimation });
      }
    }, 2300);
  };

  render() {
    const { match, appUrl } = this.props;
    const {
      searchText,
      sortCriteria,
      showModalAnimation,
      userMoveInDateRange,
      showSortCriteriaModal,
    } = this.state;

    const currentLeadStatus = LEAD_STATUS_LIST.includes(match.params.leadStatus)
      ? match.params.leadStatus
      : match.params.leadStatus === `${LEAD_STATUS_LEASE_SIGNED}`.toLowerCase() ||
        sortCriteria === APARTMENT_LOCATOR_LEADS_SORT_BY_LEASE_START_DATE
      ? LEAD_STATUS_LEASE_SIGNED
      : LEAD_STATUS_ASSIGNED;

    return (
      <div className="page-agentDashboard leads">
        <div className="navbar small">
          <SrBox width="37px" />
          <SrBox>
            <div className="logo" />
          </SrBox>
          <span className="link addLead">
            <SrLink to="/agent/add-lead">
              <span className="addLeadIcon" />
            </SrLink>
          </span>
        </div>

        <SrAgentDashboardTabBar />

        <SrAgentLeadsHeader
          leadStatus={currentLeadStatus}
          sortCriteria={sortCriteria}
          onSearchText={this._onSearchText}
          onToggleSortCriteriaModal={this._toggleSortCriteriaModal}
          onUpdateSortCriteria={this._onClickSortCriteriaOption}
        />
        <SrAgentLeadsList
          leadStatus={currentLeadStatus}
          searchText={searchText}
          sortCriteria={sortCriteria}
          userMoveInDateRange={
            sortCriteria === APARTMENT_LOCATOR_LEADS_SORT_BY_MOVE_DATE ||
            sortCriteria === APARTMENT_LOCATOR_LEADS_SORT_BY_LEASE_START_DATE
              ? userMoveInDateRange
              : null
          }
          onUpdateLeadStatus={this._onUpdateLeadStatus}
        />

        {showModalAnimation && (
          <div className="darkModal">
            <SrAgentLeadHighFiveModal appUrl={appUrl} />
          </div>
        )}

        <SrDialogActionModal
          isOpen={showSortCriteriaModal}
          title="Sort renters by:"
          onClickAction={this._onClickSortCriteriaOption}
          actions={[
            {
              id: APARTMENT_LOCATOR_LEADS_SORT_BY_STATUS_DATE,
              text: 'Recently Added',
              theme:
                sortCriteria === APARTMENT_LOCATOR_LEADS_SORT_BY_STATUS_DATE
                  ? SR_DIALOG_ACTION_THEME_ACTIVE
                  : null,
            },
            currentLeadStatus === LEAD_STATUS_LEASE_SIGNED
              ? {
                  id: APARTMENT_LOCATOR_LEADS_SORT_BY_LEASE_START_DATE,
                  text: 'Lease Start Date',
                  theme:
                    sortCriteria === APARTMENT_LOCATOR_LEADS_SORT_BY_LEASE_START_DATE
                      ? SR_DIALOG_ACTION_THEME_ACTIVE
                      : null,
                }
              : {
                  id: APARTMENT_LOCATOR_LEADS_SORT_BY_MOVE_DATE,
                  text: 'Move Date',
                  theme:
                    sortCriteria === APARTMENT_LOCATOR_LEADS_SORT_BY_MOVE_DATE
                      ? SR_DIALOG_ACTION_THEME_ACTIVE
                      : null,
                },
          ]}
          onClose={this._toggleSortCriteriaModal}
        />
      </div>
    );
  }
}

AgentLeadsPage.displayName = 'AgentLeadsPage';

export default AgentLeadsPage;
